import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { BatteryCharging, Bluetooth } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import Keyboard from '@/components/landing/keyboard'
import { AppleLogo, WindowsLogo } from '@/components/landing/brand-marks'

import { KEYBOARD_ROWS } from '@/constants/keyboard'

interface TileProps {
    children: ReactNode
    label?: string
    labelPosition?: 'top' | 'bottom'
    className?: string
}

const DEVICES = [1, 2, 3, 4]
const RINGS = [150, 104, 64]

const HIGHLIGHTED_KEYS = new Set([
    'digit-2',
    'digit-3',
    'digit-7',
    'digit-8',
    'key-w',
    'key-e',
    'key-r',
    'key-u',
    'key-i',
    'key-s',
    'key-d',
    'key-j',
    'key-k',
    'key-x',
    'key-c',
    'key-m'
])

const Tile: FC<TileProps> = ({ children, label, labelPosition = 'bottom', className }): ReactNode => (
    <figure className={cn('@container relative m-0 overflow-hidden rounded-xl border border-white/[0.08] bg-[#0b0c0e]', className)}>
        {children}

        {label && (
            <figcaption
                className={cn(
                    'absolute left-2.5 z-10 rounded-md border border-white/10 bg-[#141518]/90 px-2 py-[3px] text-[13px] leading-[18px] text-white backdrop-blur-sm',
                    labelPosition === 'top' ? 'top-2.5' : 'bottom-2.5'
                )}
            >
                {label}
            </figcaption>
        )}
    </figure>
)

const RotaryKnobTile: FC = (): ReactNode => (
    <Tile label='Rotary knob' className='kb-knob-spin aspect-[470/308]'>
        <div className='absolute inset-0 bg-[radial-gradient(60%_70%_at_68%_55%,rgb(255_255_255/0.06),transparent)]' />

        <Keyboard
            unit='23.4cqw'
            tilt={22}
            perspective={30}
            origin={[15.9, 0.8]}
            crop={{ rows: [0, 2], columns: [11, 16] }}
            className='absolute'
            style={{ left: 'calc(65.3cqw - 23.4cqw * 15.9)', top: 'calc(36.6cqw - 23.4cqw * 0.8)' }}
        />
    </Tile>
)

const UniversalLayoutTile: FC = (): ReactNode => (
    <Tile label='Universal Layout' labelPosition='top' className='aspect-[225/123]'>
        <div
            role='img'
            aria-label='Layout map that works on both macOS and Windows'
            className='absolute inset-x-[4.5%] top-[36%] bottom-[8%] flex flex-col justify-between rounded-[5px] border border-white/15 p-[2.5%]'
        >
            {KEYBOARD_ROWS.map((row, rowIndex) => (
                <div key={rowIndex} className='flex h-[13%] gap-[1.6%]'>
                    {row.map(key => (
                        <span
                            key={key.id}
                            className={cn(
                                'min-w-0 rounded-[2px] border',
                                HIGHLIGHTED_KEYS.has(key.id) ? 'border-white/70 bg-white/10' : 'border-white/20'
                            )}
                            style={{ flex: `${key.w ?? 1} 1 0%` }}
                        />
                    ))}
                </div>
            ))}
        </div>
    </Tile>
)

const MultipleDevicesTile: FC = (): ReactNode => {
    const [active, setActive] = useState(0)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const timer = window.setInterval(() => setActive(index => (index + 1) % DEVICES.length), 2200)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <Tile label='Multiple devices' labelPosition='top' className='aspect-[225/123]'>
            <div className='absolute inset-0 grid place-items-center'>
                <div className='relative grid place-items-center'>
                    {RINGS.map(size => (
                        <span
                            key={size}
                            className='absolute rounded-full border border-white/[0.06]'
                            style={{ width: size, height: size }}
                        />
                    ))}

                    <span
                        key={active}
                        aria-hidden='true'
                        className='absolute size-9 rounded-full border border-white/30 animate-[device-pulse_1.8s_ease-out_forwards] motion-reduce:hidden'
                    />

                    <span className='relative grid size-9 place-items-center rounded-full border border-white/20 bg-[#0e0f11] text-white'>
                        <Bluetooth className='size-4' aria-hidden='true' />
                    </span>
                </div>
            </div>

            <div className='absolute right-2.5 bottom-2.5 flex gap-1' aria-label='Switch between up to four paired devices' role='img'>
                {DEVICES.map((device, index) => (
                    <span
                        key={device}
                        className={cn(
                            'grid size-4 place-items-center rounded-[4px] border font-mono text-[9px] leading-none transition-colors duration-300',
                            index === active ? 'border-white bg-white text-[#0b0c0e]' : 'border-white/20 text-[#8e8f93]'
                        )}
                    >
                        {device}
                    </span>
                ))}
            </div>
        </Tile>
    )
}

const HotSwapTile: FC = (): ReactNode => (
    <Tile label='Hot swappable' className='aspect-[470/452]'>
        <div className='absolute inset-0 bg-[radial-gradient(70%_60%_at_58%_42%,rgb(255_255_255/0.07),transparent)]' />

        <Keyboard
            unit='21cqw'
            tilt={46}
            perspective={14}
            origin={[2.9, 1.8]}
            lifted='digit-2'
            crop={{ rows: [0, 4], columns: [0, 7] }}
            className='absolute'
            style={{ left: 'calc(60cqw - 21cqw * 2.9)', top: 'calc(56cqw - 21cqw * 1.8)' }}
        />
    </Tile>
)

const LightBarTile: FC = (): ReactNode => (
    <Tile label='RGB light bar' className='aspect-[470/305]'>
        <div className='absolute inset-0 bg-[radial-gradient(45%_60%_at_24%_100%,rgb(255_70_50/0.2),transparent)]' />

        <Keyboard
            unit='41.5cqw'
            tilt={18}
            perspective={24}
            origin={[0.9, 0.8]}
            crop={{ rows: [0, 1], columns: [0, 3] }}
            className='absolute'
            style={{ left: 'calc(53.6cqw - 41.5cqw * 0.9)', top: 'calc(52.6cqw - 41.5cqw * 0.8)' }}
        />
    </Tile>
)

const PlatformsTile: FC = (): ReactNode => (
    <Tile className='aspect-[225/132]'>
        <div className='absolute inset-0 [background-image:linear-gradient(rgb(255_255_255/0.045)_1px,transparent_1px),linear-gradient(90deg,rgb(255_255_255/0.045)_1px,transparent_1px)] [background-size:14px_14px] [mask-image:radial-gradient(closest-side,#000,transparent)]' />

        <div
            role='img'
            aria-label='Works with macOS and Windows'
            className='absolute inset-0 flex items-center justify-center gap-5'
        >
            <span className='grid size-11 place-items-center rounded-lg border border-white/20 bg-[#17181b] text-white shadow-[0_4px_16px_rgb(0_0_0/0.5)]'>
                <AppleLogo className='size-5' />
            </span>

            <span className='grid size-11 place-items-center rounded-lg border border-white/10 bg-[#0d0e10] text-white shadow-[0_4px_16px_rgb(0_0_0/0.5)]'>
                <WindowsLogo className='size-[18px]' />
            </span>
        </div>
    </Tile>
)

const BatteryTile: FC = (): ReactNode => (
    <Tile className='aspect-[225/132]'>
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 px-2 text-center'>
            <p className='text-[28px] leading-none tracking-[-0.02em] text-white sm:text-[32px]'>
                1,200
                {' '}
                <span className='text-[#76777b]'>
                    hrs
                </span>
            </p>

            <p className='flex items-center gap-1.5 text-[13px] text-[#8e8f93] sm:text-[14px]'>
                <BatteryCharging className='size-4' aria-hidden='true' />
                Battery life
            </p>
        </div>
    </Tile>
)

const UltraThinTile: FC = (): ReactNode => (
    <Tile label='Ultra-thin' labelPosition='top' className='aspect-[470/152]'>
        <svg
            viewBox='0 0 470 152'
            role='img'
            aria-label='Side profile of the keyboard, 26.5 millimeters tall'
            className='absolute inset-0 size-full'
        >
            <defs>
                <linearGradient id='ultra-thin-body' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#2a2a2e' />
                    <stop offset='1' stopColor='#0d0d0f' />
                </linearGradient>

                <linearGradient id='ultra-thin-cap' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#3a3a3f' />
                    <stop offset='1' stopColor='#1c1c1f' />
                </linearGradient>

                <linearGradient id='ultra-thin-fade' x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' stopColor='#0b0c0e' stopOpacity='0' />
                    <stop offset='0.55' stopColor='#0b0c0e' stopOpacity='0.15' />
                    <stop offset='1' stopColor='#0b0c0e' stopOpacity='0.9' />
                </linearGradient>
            </defs>

            {Array.from({ length: 17 }, (_, index) => (
                <rect
                    key={index}
                    x={88 + index * 26}
                    y={84}
                    width={22}
                    height={12}
                    rx={3}
                    fill='url(#ultra-thin-cap)'
                />
            ))}

            <path d='M 74 96 L 480 92 L 480 120 L 74 120 Q 66 120 66 112 L 66 104 Q 66 96 74 96 Z' fill='url(#ultra-thin-body)' />
            <path d='M 74 96 L 480 92' stroke='#55555c' strokeWidth='1.2' />
            <rect x='420' y='118' width='60' height='6' fill='#151517' />
            <rect width='470' height='152' fill='url(#ultra-thin-fade)' />

            <g stroke='#fff' strokeWidth='1.5' strokeLinecap='round'>
                <line x1='48' y1='84' x2='48' y2='120' />
                <line x1='42' y1='84' x2='54' y2='84' />
                <line x1='42' y1='120' x2='54' y2='120' />
            </g>

            <text
                x='88'
                y='66'
                fill='#fff'
                fontSize='26'
                letterSpacing='1.5'
                className='font-mono'
            >
                26.5MM
            </text>
        </svg>
    </Tile>
)

const FeaturesSection: FC = (): ReactNode => {
    return (
        <section id='features' className='relative px-6 py-24 sm:py-32'>
            <div className='mx-auto max-w-[960px]'>
                <Reveal>
                    <h2 className='text-[28px] leading-[1.2] font-medium tracking-[-0.025em] text-white sm:text-[30px]'>
                        Remarkably slim.
                        <br />
                        <span className='text-[#76777b]'>
                            Big on features.
                        </span>
                    </h2>
                </Reveal>

                <Reveal className='mt-10'>
                    <div className='grid gap-5 md:grid-cols-2'>
                        <div className='flex flex-col gap-5'>
                            <RotaryKnobTile />

                            <div className='grid grid-cols-2 gap-5'>
                                <UniversalLayoutTile />
                                <MultipleDevicesTile />
                            </div>

                            <LightBarTile />
                        </div>

                        <div className='flex flex-col gap-5'>
                            <HotSwapTile />

                            <div className='grid grid-cols-2 gap-5'>
                                <PlatformsTile />
                                <BatteryTile />
                            </div>

                            <UltraThinTile />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default FeaturesSection
